import BN from "bn.js";

/**
 * Exact decimal-string -> integer base units.
 *
 * All token quantities in this project are handled as integers. Floating point
 * is never used for an amount that ends up in a transaction, because
 * 0.1 + 0.2 !== 0.3 in IEEE-754 and a rounding error here is unrecoverable
 * on-chain.
 *
 * Rejects: empty strings, signs, exponent notation, more fractional digits
 * than the mint's decimals (silent truncation is how people lose money).
 */
export function toBaseUnits(amount: string, decimals: number): BN {
  if (!Number.isInteger(decimals) || decimals < 0 || decimals > 18) {
    throw new Error(`invalid decimals: ${decimals}`);
  }
  const s = String(amount).trim();
  if (!/^\d+(\.\d+)?$/.test(s)) {
    throw new Error(
      `invalid amount "${amount}": expected a plain non-negative decimal number ` +
        `(no sign, no exponent, no separators)`
    );
  }
  const [whole, frac = ""] = s.split(".");
  if (frac.length > decimals) {
    throw new Error(
      `amount "${amount}" has ${frac.length} fractional digits but the mint has ` +
        `${decimals} decimals; refusing to truncate`
    );
  }
  const padded = whole + frac.padEnd(decimals, "0");
  const normalized = padded.replace(/^0+(?=\d)/, "");
  return new BN(normalized, 10);
}

/** Integer base units -> exact decimal string. Inverse of toBaseUnits. */
export function fromBaseUnits(raw: BN, decimals: number): string {
  if (!Number.isInteger(decimals) || decimals < 0 || decimals > 18) {
    throw new Error(`invalid decimals: ${decimals}`);
  }
  const s = raw.toString(10).padStart(decimals + 1, "0");
  const whole = s.slice(0, s.length - decimals);
  const frac = decimals === 0 ? "" : s.slice(s.length - decimals).replace(/0+$/, "");
  return frac ? `${whole}.${frac}` : whole;
}

/**
 * Opening price of the pool, in quote units per one base unit, as an exact
 * decimal string. Display only -- never fed back into a transaction.
 */
export function openingPrice(
  baseRaw: BN,
  baseDecimals: number,
  quoteRaw: BN,
  quoteDecimals: number,
  precision = 18
): string {
  if (baseRaw.isZero()) throw new Error("base liquidity is zero");
  // price = (quoteRaw / 10^qd) / (baseRaw / 10^bd)
  //       = quoteRaw * 10^bd / (baseRaw * 10^qd)
  const num = quoteRaw.mul(new BN(10).pow(new BN(baseDecimals + precision)));
  const den = baseRaw.mul(new BN(10).pow(new BN(quoteDecimals)));
  const scaled = num.div(den);
  return fromBaseUnits(scaled, precision);
}

/** Constant-product output for a swap, ignoring fees. Used only as a sanity bound. */
export function constantProductOut(reserveIn: BN, reserveOut: BN, amountIn: BN): BN {
  if (reserveIn.isZero() || reserveOut.isZero()) return new BN(0);
  const k = reserveIn.mul(reserveOut);
  const newIn = reserveIn.add(amountIn);
  const newOut = k.div(newIn).add(new BN(1));
  return reserveOut.sub(newOut);
}
